import React from "react";
import { Box, Button, Menu, MenuItem, Typography } from "@mui/material";
import { ArrowDropDown } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { ResponseSubjectType } from "../../types";

interface Props {
  campaignId: string;
  campaignReleased: boolean;
  selectedTeamId?: string;
  selectedUserId?: string;
  subjectType: ResponseSubjectType;
  reverse: boolean;
}

const ExportMenu = ({
  campaignReleased,
  campaignId,
  selectedTeamId,
  selectedUserId,
  subjectType,
  reverse,
}: Props) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  //
  // Event Handlers
  //

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const buildQueryString = () => {
    const params = new URLSearchParams();
    if (selectedUserId) {
      params.set("userId", selectedUserId);
    }
    if (selectedTeamId) {
      params.set("teamId", selectedTeamId);
    }
    if (subjectType) {
      params.set("subjectType", subjectType);
    }
    params.set("direction", reverse ? "given" : "received");
    const query = params.toString();
    return query ? `?${query}` : "";
  };

  const handleDownloadPdf = () => {
    handleClose();
    if (selectedUserId) {
      window.location.href = `/api/campaigns/${campaignId}/users/${selectedUserId}/pdf`;
    } else if (selectedTeamId) {
      window.location.href = `/api/campaigns/${campaignId}/teams/${selectedTeamId}/pdf`;
    } else {
      window.location.href = `/api/campaigns/${campaignId}/pdf`;
    }
  };

  const handleDownloadSummaryCsv = () => {
    handleClose();
    const query = buildQueryString();
    window.location.href = `/api/campaigns/${campaignId}/results/summary.csv${query}`;
  };

  const handleDownloadRawCsv = () => {
    handleClose();
    const query = buildQueryString();
    window.location.href = `/api/campaigns/${campaignId}/results/raw.csv${query}`;
  };

  return (
    <>
      <Button
        id="download-button"
        aria-controls={open ? "download-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        variant="contained"
        endIcon={<ArrowDropDown />}
      >
        Download Results
      </Button>
      <Menu
        id="download-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            width: 265,
          }}
        >
          <MenuItem>
            <Button
              sx={{ color: "white", justifyContent: "flex-start" }}
              onClick={handleDownloadSummaryCsv}
            >
              Averaged Results
            </Button>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              ({t("surveys.analysis.export.averagedResults.fileType")})
            </Typography>
          </MenuItem>
          <MenuItem>
            <Button
              sx={{ color: "white", justifyContent: "flex-start" }}
              onClick={handleDownloadRawCsv}
            >
              Raw Data
            </Button>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              ({t("surveys.analysis.export.rawData.fileType")})
            </Typography>
          </MenuItem>
          <MenuItem>
            <Button
              sx={{ color: "white", justifyContent: "flex-start" }}
              onClick={handleDownloadPdf}
            >
              {t("surveys.analysis.export.releasedResults.button")}
            </Button>
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
              ({t("surveys.analysis.export.releasedResults.fileType")})
            </Typography>
          </MenuItem>
        </Box>
      </Menu>
    </>
  );
};

export default ExportMenu;
